import { useState, useEffect, useCallback } from 'react';
import { getClient } from '@retreats/supabase-adapter';
import { useAuthContext } from './authContext';
import { TERMS_VERSION } from '../constants';
import { classifyError } from '../utils/errors';
import { isE2E } from '../utils/env';
import type { TermsGateState } from '../types/updates';

interface TermsAcceptance {
  state: TermsGateState;
  accept: () => Promise<void>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySupabaseClient = any;

export function useTermsAcceptance(): TermsAcceptance {
  const { session } = useAuthContext();
  const [state, setState] = useState<TermsGateState>(
    isE2E() ? { status: 'accepted' } : { status: 'loading' },
  );

  useEffect(() => {
    if (isE2E() || !session) return;
    let active = true;
    const client: AnySupabaseClient = getClient();
    void (async () => {
      try {
        const { data, error } = await client
          .from('profiles')
          .select('terms_version')
          .eq('id', session.userId)
          .single();
        if (!active) return;
        if (!error && data?.terms_version === TERMS_VERSION) {
          setState({ status: 'accepted' });
        } else {
          // Fail open: null version, mismatched version, or fetch error all show the prompt
          setState({ status: 'pending' });
        }
      } catch {
        // Fail open — never block the user on an unknown error
        if (active) setState({ status: 'pending' });
      }
    })();
    return () => { active = false; };
  }, [session?.userId]);

  const accept = useCallback(async () => {
    if (!session) return;
    const client: AnySupabaseClient = getClient();
    const { error } = await client
      .from('profiles')
      .update({
        terms_accepted_at: new Date().toISOString(),
        terms_version: TERMS_VERSION,
      })
      .eq('id', session.userId);

    if (error) {
      throw classifyError(error, 'TERMS_ACCEPT_FAILED');
    }
    setState({ status: 'accepted' });
  }, [session?.userId]);

  return { state, accept };
}
